import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { barrierApi, restaurantApi, authApi } from '../../services/api';
import { useT } from '../../i18n';
import { useModuleEnabled } from '../../store';
import { formatDate } from '../../utils/date';

function StatCard({ icon, label, value, sub, color = 'var(--primary)', onClick, loading, bar, gradient }) {
  return (
    <div
      style={{
        ...S.card,
        cursor: onClick ? 'pointer' : 'default',
        background: gradient || 'var(--card)',
      }}
      onClick={onClick}
    >
      <div style={{ ...S.cardIcon, background: color + '22', color }}>{icon}</div>
      <div style={{ ...S.cardValue, color: 'var(--text)' }}>{loading ? <span style={S.skeleton}>—</span> : value}</div>
      <div style={{ ...S.cardLabel, color: 'var(--text2)' }}>{label}</div>
      {sub && <div style={{ ...S.cardSub, color: 'var(--text3)' }}>{sub}</div>}
      {bar != null && (
        <div style={{ ...S.barTrack, background: 'var(--bg2)' }}>
          <div style={{ ...S.barFill, width: `${Math.max(0, Math.min(100, bar))}%`, background: bar > 60 ? 'var(--success)' : bar > 30 ? 'var(--warning)' : 'var(--danger)' }} />
        </div>
      )}
    </div>
  );
}

function StatusDot({ ok }) {
  const color = ok === true ? 'var(--success)' : ok === false ? 'var(--danger)' : 'var(--warning)';
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 6 }} />;
}

const METHOD_COLORS = { internet: '#3b82f6', bluetooth: '#8b5cf6', ble: '#8b5cf6', admin: '#f59e0b', unknown: '#475569' };

export default function Dashboard() {
  const t = useT();
  const navigate = useNavigate();
  const socketRef = useRef(null);

  const barrierEnabled = useModuleEnabled('barrier');
  const restaurantEnabled = useModuleEnabled('restaurant');

  const [barriers, setBarriers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [userCount, setUserCount] = useState(null);
  const [systemStatus, setSystemStatus] = useState({ api: null, mqtt: null, db: null });
  const [recentLogs, setRecentLogs] = useState([]);
  const [todayOpens, setTodayOpens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(null);

  const onlineCount = barriers.filter(b => b.isOnline).length;
  const onlinePct = barriers.length ? Math.round((onlineCount / barriers.length) * 100) : 0;

  const fetchRecentLogs = useCallback(async (barrierList) => {
    const BAKU_OFFSET_MS = 4 * 60 * 60 * 1000;
    const nowBaku = new Date(Date.now() + BAKU_OFFSET_MS);
    const todayBakuDate = nowBaku.getUTCDate();
    const todayBakuMonth = nowBaku.getUTCMonth();
    const todayBakuYear = nowBaku.getUTCFullYear();
    const all = [];
    const targets = barrierList;
    await Promise.allSettled(
      targets.map(b =>
        barrierApi.logs(b.id).then(d => {
          const rows = Array.isArray(d) ? d : (d.data || d.logs || []);
          rows.forEach(r => all.push({ ...r, barrierName: b.name || b.location || `#${b.id}` }));
        })
      )
    );
    all.sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp));
    setRecentLogs(all.slice(0, 10));
    const opens = all.filter(r => {
      const ts = new Date((r.createdAt || r.timestamp));
      const tsBaku = new Date(ts.getTime() + BAKU_OFFSET_MS);
      return tsBaku.getUTCDate() === todayBakuDate &&
             tsBaku.getUTCMonth() === todayBakuMonth &&
             tsBaku.getUTCFullYear() === todayBakuYear &&
             r.success !== false;
    });
    setTodayOpens(opens.length);
  }, []);

  const checkSystemStatus = useCallback(async () => {
    setSystemStatus(prev => ({ ...prev, api: true }));
    try {
      const debug = await barrierApi.debug();
      setSystemStatus(prev => ({
        ...prev,
        mqtt: debug?.mqttConnected ?? debug?.mqtt ?? null,
        db: debug?.dbConnected ?? debug?.db ?? true,
      }));
    } catch {
      setSystemStatus(prev => ({ ...prev, mqtt: null, db: true }));
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const [barrierRes, restaurantRes] = await Promise.allSettled([
        barrierApi.list(),
        restaurantApi.list(),
      ]);

      let bList = [];
      if (barrierRes.status === 'fulfilled') {
        bList = Array.isArray(barrierRes.value) ? barrierRes.value : (barrierRes.value?.data || []);
        setBarriers(bList);
      }
      if (restaurantRes.status === 'fulfilled') {
        const rList = Array.isArray(restaurantRes.value) ? restaurantRes.value : (restaurantRes.value?.data || []);
        setRestaurants(rList);
      }

      authApi.stats().then(d => setUserCount(d?.total ?? d?.count ?? null)).catch(() => {
        authApi.users().then(d => {
          const list = Array.isArray(d) ? d : (d?.data || []);
          setUserCount(list.length);
        }).catch(() => setUserCount(null));
      });

      checkSystemStatus();
      if (bList.length) fetchRecentLogs(bList);
      setLoading(false);
    };
    init();

    const interval = setInterval(() => {
      setBarriers(prev => { if (prev.length) fetchRecentLogs(prev); return prev; });
    }, 30000);

    // Socket.io üçün /api/v1 hissəsini çıxar — WebSocket base URL lazımdır
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/api\/v1\/?$/, '');
    const socket = io(`${apiBase}/barriers`, { transports: ['polling', 'websocket'], upgrade: true });
    socketRef.current = socket;
    socket.on('connect', () => { setWsConnected(true); socket.emit('joinAdmin'); });
    socket.on('disconnect', () => setWsConnected(false));
    socket.on('connect_error', () => setWsConnected(false));
    socket.on('statusChange', ({ barrierId, isOnline }) => {
      setBarriers(prev => prev.map(b => b.id === barrierId ? { ...b, isOnline } : b));
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, [fetchRecentLogs, checkSystemStatus]);

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={{ ...S.title, color: 'var(--text)' }}>{t('dash_title')}</h1>
          <p style={{ ...S.subtitle, color: 'var(--text3)' }}>
            {formatDate(new Date(), 'full')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{
            ...S.badge,
            background: socketRef.current?.connected ? '#10b98122' : '#f59e0b22',
            color: socketRef.current?.connected ? 'var(--success)' : 'var(--warning)'
          }}>
            <StatusDot ok={socketRef.current?.connected} /> Real-time
          </div>
          <div style={{ ...S.badge, background: 'var(--primary)22', color: 'var(--primary)' }}>
            {t('active')}
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div style={S.grid}>
        {barrierEnabled && (
          <StatCard
            icon="⊞"
            label={t('nav_barriers')}
            value={barriers.length}
            sub={`${onlineCount} online / ${barriers.length - onlineCount} offline`}
            color="#3b82f6"
            onClick={() => navigate('/barriers')}
            loading={loading}
            bar={onlinePct}
          />
        )}
        {restaurantEnabled && (
          <StatCard
            icon="◉"
            label={t('nav_restaurants')}
            value={restaurants.length}
            sub={`${restaurants.length} ${t('active')}`}
            color="#10b981"
            onClick={() => navigate('/restaurants')}
            loading={loading}
          />
        )}
        <StatCard
          icon="◎"
          label={t('dash_total_users')}
          value={userCount ?? '—'}
          sub={userCount ? t('status_active') : t('loading')}
          color="#f59e0b"
          loading={loading && userCount === null}
        />
        {barrierEnabled && (
          <StatCard
            icon="◆"
            label="Bu gün açılma"
            value={todayOpens ?? '—'}
            sub="Uğurlu giriş"
            color="#06b6d4"
            loading={loading && todayOpens === null}
          />
        )}
      </div>

      <div style={S.row}>
        {/* Quick Actions */}
        <div style={{ ...S.panel, background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div style={{ ...S.panelHead, color: 'var(--text2)' }}>Sürətli Əməliyyatlar</div>
          <div style={S.quickActions}>
            {[
              barrierEnabled && { label: t('nav_barriers'), icon: '⊞', path: '/barriers', color: '#3b82f6' },
              restaurantEnabled && { label: t('nav_restaurants'), icon: '◉', path: '/restaurants', color: '#10b981' },
              { label: t('nav_analytics'), icon: '▲', path: '/analytics', color: '#f59e0b' },
              { label: t('nav_users'), icon: '◎', path: '/users', color: '#8b5cf6' },
            ].filter(Boolean).map((a, i) => (
              <button key={i} style={{ ...S.qaBtn, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text2)' }}
                onClick={() => navigate(a.path)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = a.color; e.currentTarget.style.color = 'var(--text)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}>
                <span style={{ color: a.color, fontWeight: 700 }}>{a.icon}</span> {a.label}
              </button>
            ))}
          </div>

          {/* Barrier online bar */}
          {barrierEnabled && barriers.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: 'var(--text3)' }}>Şlaqbaum aktivliyi</span>
                <span style={{ fontSize: 13, color: onlinePct > 60 ? 'var(--success)' : 'var(--warning)', fontWeight: 600 }}>{onlinePct}%</span>
              </div>
              <div style={{ ...S.barTrack, background: 'var(--bg2)' }}>
                <div style={{ ...S.barFill, width: `${onlinePct}%`, background: onlinePct > 60 ? 'var(--success)' : onlinePct > 30 ? 'var(--warning)' : 'var(--danger)', transition: 'width 0.5s' }} />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
                {barriers.slice(0, 4).map(b => (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text3)' }}>
                    <StatusDot ok={b.isOnline} />
                    {b.name || b.location || `#${b.id}`}
                  </div>
                ))}
                {barriers.length > 4 && <span style={{ fontSize: 12, color: 'var(--text3)' }}>+{barriers.length - 4} daha</span>}
              </div>
            </div>
          )}
        </div>

        {/* System Status */}
        <div style={{ ...S.panel, background: 'var(--card)', border: '1px solid var(--border)' }}>
          <div style={{ ...S.panelHead, color: 'var(--text2)' }}>{t('dash_system')}</div>
          {[
            { label: 'Backend API', key: 'api', ok: systemStatus.api },
            { label: 'MQTT Broker', key: 'mqtt', ok: systemStatus.mqtt },
            { label: t('svc_db'), key: 'db', ok: systemStatus.db },
            { label: 'WebSocket', key: 'ws', ok: wsConnected },
          ].map((item) => (
            <div key={item.key} style={{ ...S.statusRow, borderBottom: '1px solid var(--border)' }}>
              <span style={{ ...S.statusLabel, color: 'var(--text2)' }}>{item.label}</span>
              <span style={{
                ...S.statusBadge,
                background: item.ok === null ? 'var(--warning)22' : item.ok ? 'var(--success)22' : 'var(--danger)22',
                color: item.ok === null ? 'var(--warning)' : item.ok ? 'var(--success)' : 'var(--danger)'
              }}>
                <StatusDot ok={item.ok} />
                {item.ok === null ? 'Yoxlanılır' : item.ok ? t('svc_online') : t('svc_offline')}
              </span>
            </div>
          ))}

          {barrierEnabled && (
            <div style={{ marginTop: 16, padding: '12px 0 0', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Şlaqbaum xülasəsi</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  { label: t('total'), value: barriers.length, color: 'var(--primary)' },
                  { label: t('bar_online'), value: onlineCount, color: 'var(--success)' },
                  { label: t('bar_offline'), value: barriers.length - onlineCount, color: 'var(--danger)' },
                  { label: 'Bu gün', value: todayOpens ?? '—', color: '#06b6d4' },
                ].map(m => (
                  <div key={m.label} style={{ background: 'var(--bg2)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: m.color }}>{m.value}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{ ...S.panel, background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ ...S.panelHead, color: 'var(--text2)' }}>{t('dash_recent')}</div>
          <button style={{ ...S.refreshBtn, border: '1px solid var(--border)', color: 'var(--text3)' }}
            onClick={() => fetchRecentLogs(barriers)}>
            ⟳ Yenilə
          </button>
        </div>
        {recentLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 14 }}>
            {loading ? t('loading') : 'Hələlik aktivlik yoxdur'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recentLogs.map((log, i) => {
              const method = (log.method || log.openMethod || 'unknown').toLowerCase();
              const methodColor = METHOD_COLORS[method] || METHOD_COLORS.unknown;
              return (
                <div key={i} style={{ ...S.logRow, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                  <div style={{ ...S.logMethod, background: methodColor + '22', color: methodColor }}>
                    {method === 'internet' ? '🌐' : method === 'bluetooth' || method === 'ble' ? '📶' : method === 'admin' ? '👤' : '?'}
                    {' '}{method}
                  </div>
                  <div style={{ flex: 1, fontSize: 13, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.barrierName}
                    {log.user?.name || log.userName ? ` — ${log.user?.name || log.userName}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    {formatDate(log.createdAt || log.timestamp, 'compact')}
                  </div>
                  <div style={{ ...S.logStatus, background: log.success === false ? 'var(--danger)22' : 'var(--success)22', color: log.success === false ? 'var(--danger)' : 'var(--success)' }}>
                    {log.success === false ? '✗' : '✓'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Roadmap */}
      <div style={{ ...S.panel, background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div style={{ ...S.panelHead, color: 'var(--text2)' }}>Növbəti Mərhələlər</div>
        <div style={S.roadmap}>
          {[
            { phase: 'Mərhələ 1', label: 'Şlaqbaum Sistemi', color: 'var(--success)', dot: '✓' },
            { phase: 'Mərhələ 2', label: 'Restoran QR Sistemi', color: 'var(--success)', dot: '✓' },
            { phase: 'Mərhələ 3', label: 'Taxi İnteqrasiyası', color: 'var(--warning)', dot: '○' },
            { phase: 'Mərhələ 4', label: 'Bərbər/Salon', color: 'var(--text3)', dot: '○' },
            { phase: 'Mərhələ 5', label: 'Super-App + Wallet', color: 'var(--text3)', dot: '○' },
          ].map((r, i) => (
            <div key={i} style={{ ...S.roadmapItem, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{r.phase}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ color: r.color, fontWeight: 700 }}>{r.dot}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
  title: { fontSize: 28, fontWeight: 800, marginBottom: 4 },
  subtitle: { fontSize: 14 },
  badge: { padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  card: { border: '1px solid var(--border)', borderRadius: 12, padding: 20, transition: 'border-color 0.2s, transform 0.15s', cursor: 'default' },
  cardIcon: { width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, marginBottom: 16 },
  cardValue: { fontSize: 32, fontWeight: 800, marginBottom: 4 },
  cardLabel: { fontSize: 13, fontWeight: 500 },
  cardSub: { fontSize: 12, marginTop: 4 },
  skeleton: { background: 'var(--border)', borderRadius: 4, color: 'transparent' },
  barTrack: { height: 6, borderRadius: 4, overflow: 'hidden', marginTop: 8 },
  barFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  panel: { borderRadius: 12, padding: 20, marginBottom: 16 },
  panelHead: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 },
  quickActions: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 0 },
  qaBtn: { borderRadius: 8, padding: '10px 14px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s', textAlign: 'left' },
  statusRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' },
  statusLabel: { fontSize: 14 },
  statusBadge: { padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center' },
  refreshBtn: { background: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  logRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 8 },
  logMethod: { padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
  logStatus: { padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700, flexShrink: 0 },
  roadmap: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 },
  roadmapItem: { flex: '1 1 160px', borderRadius: 8, padding: 14 },
};
